import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { MainLayout } from '@/components/layouts/main-layout';
import { AuthLayout } from '@/components/layouts/auth-layout';
import { LoginPage } from '@/features/auth/components/login-page';
import { RegisterPage } from '@/features/auth/components/register-page';
import { DashboardPage } from '@/features/auth/components/dashboard-page';
import { ChangePasswordPage } from '@/features/auth/components/change-password-page';
import { BenchmarkPage } from '@/features/benchmark/BenchmarkPage';
import { ContinuousScanPage } from '@/features/continuous-scan/ContinuousScanPage';
import { CycleCountPage } from '@/features/cycle-count/CycleCountPage';
import { AdminUsersPage } from '@/features/admin-users/components/admin-users-page';

const CHANGE_PASSWORD_PATH = '/change-password';

function LoadingScreen() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.mustChangePassword && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.mustChangePassword && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  if (user?.role !== 'Admin') {
    return <Navigate to="/scan" replace />;
  }

  return <>{children}</>;
}

/**
 * Gate for `/change-password`. Requires authentication but deliberately does
 * NOT enforce the `mustChangePassword` redirect — otherwise the user would
 * loop on this very route. Anyone authenticated may visit and update their
 * password here (admins resetting their own + forced users + voluntary
 * changes once we surface a link).
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    // If we have a forced password change pending, route the user there
    // instead of /scan so we don't immediately bounce them.
    if (user?.mustChangePassword) {
      return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
    }
    return <Navigate to="/scan" replace />;
  }

  return <>{children}</>;
}

export function AppRouter() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <AuthLayout>
              <RegisterPage />
            </AuthLayout>
          </PublicRoute>
        }
      />
      <Route
        path={CHANGE_PASSWORD_PATH}
        element={
          <RequireAuth>
            <AuthLayout>
              <ChangePasswordPage />
            </AuthLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cycle-count"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CycleCountPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scan"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ContinuousScanPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/benchmark"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BenchmarkPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <MainLayout>
              <AdminUsersPage />
            </MainLayout>
          </AdminRoute>
        }
      />
      <Route path="/" element={<Navigate to="/scan" replace />} />
      <Route path="*" element={<Navigate to="/scan" replace />} />
    </Routes>
  );
}
